import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum, fmtPct } from "./formatters";

// Phase 3 (B2): Modules summary — mirrors PDF page 1.
//
// Section contents:
//   - Title "Modules"
//   - Per-module table: Numéro, Module, current m², prior m², Diff., %, Info
//   - Total m² row
//
// Round 5: the client asked to delete the "Répartition par catégorie" donut too
// (it was the last chart in the section pipeline). Modules is now a TABLE-ONLY
// portrait page. Preview.js MODULES_PAGES stays 1.
//
// Props
//   data         response from GET /v1/repartition-report/modules-summary
//   building     { name, address }
//   periodLabel  string (e.g. "01.01.2024 - 31.12.2024")
//   periodYears  { current: number, prior?: number } — used for chart legend
//   compareEnabled bool — when false, hides the prior column + Diff + %
//   whiteLabelling, logoUrl  for the page frame
//   pageNumber, totalPages

const ModulesSummary = ({
  data,
  building,
  periodLabel,
  periodYears,
  compareEnabled,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const modules = data?.modules || [];
  const totals = data?.totals || {};

  const currentYear = periodYears?.current ?? "Current";
  const priorYear   = periodYears?.prior   ?? "Prior";

  return (
    <A4PageFrame
      building={building}
      periodLabel={periodLabel}
      sectionTitle="Modules"
      whiteLabelling={whiteLabelling}
      logoUrl={logoUrl}
      pageNumber={basePageNumber}
      totalPages={grandTotalPages}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #00033F" }}>
              <th style={th}>Numéro</th>
              <th style={th}>Module</th>
              <th style={thNum}>{currentYear}</th>
              {compareEnabled && <th style={thNum}>{priorYear}</th>}
              {compareEnabled && <th style={thNum}>Diff.</th>}
              {compareEnabled && <th style={thNum}>%</th>}
              <th style={thLeft}>Info</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.surface}>
                <td style={td}>{m.surface}</td>
                <td style={td}>{m.name || ""}</td>
                <td style={tdNum}>{fmtNum(m.main_value)}</td>
                {compareEnabled && <td style={tdNum}>{fmtNum(m.compare_value)}</td>}
                {compareEnabled && <td style={tdNum}>{fmtNum(m.diff)}</td>}
                {compareEnabled && <td style={tdNum}>{fmtPct(m.diff_pct)}</td>}
                <td style={td}>{m.unit || ""}</td>
              </tr>
            ))}
            <tr style={{ borderTop: "1px solid #00033F", fontWeight: 700 }}>
              <td style={td} />
              <td style={td}>Total m²</td>
              <td style={tdNum}>{fmtNum(totals.main_value)}</td>
              {compareEnabled && <td style={tdNum}>{fmtNum(totals.compare_value)}</td>}
              {compareEnabled && <td style={tdNum}>{fmtNum(totals.diff)}</td>}
              {compareEnabled && <td style={tdNum}>{fmtPct(totals.diff_pct)}</td>}
              <td style={td} />
            </tr>
          </tbody>
        </table>
      </A4PageFrame>
  );
};

// ─── inline styles (kept inline so html-to-image captures them without CSS files) ───

// M1.1: header bumped from 10pt to 11pt for parity. Body already at 10pt.
const th = { padding: "4px 6px", fontWeight: 600, textAlign: "left", fontSize: 11 };
const thNum = { ...th, textAlign: "right" };
const thLeft = { ...th, textAlign: "left", width: 50 };
const td = { padding: "3px 6px", fontSize: 10, borderBottom: "1px solid rgba(0,3,63,0.05)" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default ModulesSummary;
